// Cliente HTTP com refresh automático de token (proativo + reativo)

const TOKEN_KEY = 'm365t-access-token'
const REFRESH_KEY = 'm365t-refresh-token'

export const tokens = {
  get access() { return localStorage.getItem(TOKEN_KEY) },
  get refresh() { return localStorage.getItem(REFRESH_KEY) },
  set(access: string, refresh: string) {
    localStorage.setItem(TOKEN_KEY, access)
    localStorage.setItem(REFRESH_KEY, refresh)
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
  },
}

/**
 * Decodifica payload do JWT (segunda parte) sem validar assinatura.
 * Retorna null se token inválido ou ausente. Usado só pra checar `exp`.
 */
function decodeJwt(token: string | null): { exp?: number } | null {
  if (!token) return null
  const parts = token.split('.')
  if (parts.length < 2) return null
  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4)
    return JSON.parse(atob(padded))
  } catch {
    return null
  }
}

/** Retorna true se o access token expira nos próximos N segundos (default 60). */
function isTokenExpiringSoon(seconds = 60): boolean {
  const payload = decodeJwt(tokens.access)
  if (!payload?.exp) return true
  const nowSec = Math.floor(Date.now() / 1000)
  return payload.exp - nowSec < seconds
}

/** Retorna true se já passou da validade. */
function isTokenExpired(): boolean {
  const payload = decodeJwt(tokens.access)
  if (!payload?.exp) return true
  return payload.exp <= Math.floor(Date.now() / 1000)
}

let refreshPromise: Promise<boolean> | null = null

async function refreshAccessToken(): Promise<boolean> {
  // Dedup: várias requests paralelas usam a MESMA promise
  if (refreshPromise) return refreshPromise

  const refreshToken = tokens.refresh
  if (!refreshToken) return false

  refreshPromise = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      })
      if (!res.ok) {
        // Refresh token também expirou ou foi revogado
        tokens.clear()
        return false
      }
      const data = await res.json()
      tokens.set(data.accessToken, data.refreshToken)
      return true
    } catch (err) {
      console.warn('[auth] refresh failed:', err)
      tokens.clear()
      return false
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

export class ApiError extends Error {
  constructor(public status: number, public data: any, message?: string) {
    super(message || `HTTP ${status}`)
  }
}

async function request<T = any>(
  method: string,
  path: string,
  body?: any,
  retried = false
): Promise<T> {
  // 1. Refresh proativo: se token vai expirar em <60s, renovar ANTES de mandar
  if (!retried && tokens.access && tokens.refresh && isTokenExpiringSoon(60)) {
    await refreshAccessToken()
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = tokens.access
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  // 2. Refresh reativo: 401 → tenta refresh + retry uma vez
  if (res.status === 401 && !retried && tokens.refresh) {
    const refreshed = await refreshAccessToken()
    if (refreshed) {
      return request<T>(method, path, body, true)
    }
    // Refresh falhou: token expirado de verdade, manda pra login
    tokens.clear()
    if (window.location.pathname !== '/login') {
      window.location.href = '/login'
    }
    throw new ApiError(401, null, 'Sessão expirada')
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  let data: any = null
  try { data = text ? JSON.parse(text) : null } catch { data = text }

  if (!res.ok) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`
    throw new ApiError(res.status, data, msg)
  }

  return data as T
}

export const api = {
  get: <T = any>(path: string) => request<T>('GET', path),
  post: <T = any>(path: string, body?: any) => request<T>('POST', path, body),
  patch: <T = any>(path: string, body?: any) => request<T>('PATCH', path, body),
  put: <T = any>(path: string, body?: any) => request<T>('PUT', path, body),
  delete: <T = any>(path: string) => request<T>('DELETE', path),
}

// Expõe pra debug no console (window.__auth.tokens, window.__auth.isTokenExpired())
if (typeof window !== 'undefined') {
  ;(window as any).__auth = { tokens, isTokenExpiringSoon, isTokenExpired, decodeJwt }
}

// ----- Endpoints tipados -----

export interface UserInfo {
  id: string
  email: string
  displayName: string
  role: string
  twoFactorEnabled: boolean
}

export interface LoginResponse {
  accessToken: string
  refreshToken: string
  accessTokenExpiresAt: string
  user: UserInfo
}

export interface Tenant {
  id: string
  displayName: string
  tenantId: string
  primaryDomain: string
  status: number
  consentGrantedAt: string | null
  lastValidatedAt: string | null
  lastValidationError: string | null
  organizationDisplayName: string | null
  userCount: number | null
  licensedUserCount: number | null
  graphInfoUpdatedAt: string | null
  createdAt: string
}

export interface DiscoveryJob {
  id: string
  projectId: string
  status: string
  currentStep: string
  progressCurrent: number
  progressTotal: number
  scheduledStartAt: string | null
  startedAt: string | null
  completedAt: string | null
  errorMessage: string | null
  usersCount: number | null
  groupsCount: number | null
  domainsCount: number | null
  conflictsCount: number | null
}

export interface Project {
  id: string
  name: string
  description: string | null
  type: string
  sourceTenantId: string
  destinationTenantId: string | null
  sourceTenantDisplayName: string | null
  destinationTenantDisplayName: string | null
  domain: string | null
  status: string
  productionSide: string
  scopeType: string
  scopeUpns: string[] | null
  createdByUserId: string
  createdAt: string
  updatedAt: string
  latestDiscovery: DiscoveryJob | null
}

export const auth = {
  login: (email: string, password: string, twoFactorCode?: string) =>
    api.post<LoginResponse | { requiresTwoFactor: true; message: string }>('/api/auth/login', {
      email, password, twoFactorCode,
    }),
  me: () => api.get<UserInfo>('/api/auth/me'),
  logout: (refreshToken: string) => api.post('/api/auth/logout', { refreshToken }),
  setup2fa: () => api.post<{ secret: string; qrCodeUri: string; manualEntryKey: string }>('/api/auth/2fa/setup'),
  enable2fa: (code: string) => api.post('/api/auth/2fa/enable', { code }),
  disable2fa: (code: string) => api.post('/api/auth/2fa/disable', { code }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/api/auth/change-password', { currentPassword, newPassword }),
}

export const tenants = {
  list: () => api.get<Tenant[]>('/api/tenants'),
  get: (id: string) => api.get<Tenant>(`/api/tenants/${id}`),
  connectUrl: (note?: string) =>
    api.post<{ consentUrl: string; stateToken: string }>('/api/tenants/connect-url', { note }),
  validate: (id: string) => api.post(`/api/tenants/${id}/validate`),
  disconnect: (id: string) => api.delete(`/api/tenants/${id}`),
}

export const projects = {
  list: () => api.get<Project[]>('/api/projects'),
  get: (id: string) => api.get<Project>(`/api/projects/${id}`),
  create: (data: any) => api.post<Project>('/api/projects', data),
  update: (id: string, data: any) => api.patch<Project>(`/api/projects/${id}`, data),
  cancel: (id: string) => api.post(`/api/projects/${id}/cancel`),
  delete: (id: string, confirmName: string) =>
    api.delete(`/api/projects/${id}?confirmName=${encodeURIComponent(confirmName)}`),
}

export const discovery = {
  current: (projectId: string) => api.get<DiscoveryJob>(`/api/projects/${projectId}/discovery/current`),
  cancel: (projectId: string, jobId: string) =>
    api.post(`/api/projects/${projectId}/discovery/${jobId}/cancel`),
  rerun: (projectId: string) => api.post<DiscoveryJob>(`/api/projects/${projectId}/discovery/rerun`),
  users: (projectId: string, params?: { side?: string; inScopeOnly?: boolean; conflictsOnly?: boolean; skip?: number; take?: number }) => {
    const q = new URLSearchParams()
    if (params?.side) q.set('side', params.side)
    if (params?.inScopeOnly) q.set('inScopeOnly', 'true')
    if (params?.conflictsOnly) q.set('conflictsOnly', 'true')
    if (params?.skip) q.set('skip', String(params.skip))
    if (params?.take) q.set('take', String(params.take))
    return api.get<{ items: any[]; total: number; skip: number; take: number }>(
      `/api/projects/${projectId}/discovery/users?${q.toString()}`)
  },
  groups: (projectId: string, side?: string) =>
    api.get<any[]>(`/api/projects/${projectId}/discovery/groups${side ? '?side=' + side : ''}`),
  domains: (projectId: string, side?: string) =>
    api.get<any[]>(`/api/projects/${projectId}/discovery/domains${side ? '?side=' + side : ''}`),
  summary: (projectId: string) => api.get<any>(`/api/projects/${projectId}/discovery/summary`),
}

export const users = {
  list: () => api.get<any[]>('/api/users'),
  create: (data: any) => api.post('/api/users', data),
  deactivate: (id: string) => api.patch(`/api/users/${id}/deactivate`),
  activate: (id: string) => api.patch(`/api/users/${id}/activate`),
}

// ----- Sprint 3 — Planning -----

export interface TenantDomainSummary {
  name: string
  isDefault: boolean
  isVerified: boolean
  userCount: number
}

export interface TenantDiscoverySummary {
  tenantId: string
  organizationName: string
  totalUsers: number
  domains: TenantDomainSummary[]
  collectedAt: string
}

export interface PlanItem {
  id: string
  planId: string
  discoveredUserId: string
  sourceUpn: string
  sourceDomain: string
  sourceDisplayName: string | null
  sourceJobTitle: string | null
  sourceDepartment: string | null
  mailboxSizeBytes: number | null
  mailboxItemCount: number | null
  oneDriveSizeBytes: number | null
  oneDriveFileCount: number | null
  isSelected: boolean
  strategy: 'CreateNew' | 'LinkExisting'
  destinationUpn: string | null
  linkedDestinationUserId: string | null
  linkedDestinationUpn: string | null
  assignedLicenseSkuId: string | null
  assignedLicenseSkuName: string | null
  contentSelectionJson: string
  validationsJson: string | null
  /// Endereço que a conta terá quando a migração terminar. Enquanto o destino não for este,
  /// o aviso de OneNote não é enviado — a URL do OneDrive acompanha o UPN e o link morreria.
  finalUpn: string | null
}

export interface Plan {
  id: string
  projectId: string
  status: string
  strategyId: string | null
  strategyName: string | null
  defaultDestinationDomain: string | null
  domains: string[]
  folderPolicyJson: string | null
  defaultContentScopeJson: string | null
  waveBins: number[]
  defaultOwnerUpn: string | null
  executionMode: string
  scheduledStartAt: string | null
  approvedAt: string | null
  createdAt: string
  updatedAt: string
  summary: PlanSummary
}

export interface PlanSummary {
  totalUsers: number
  selectedUsers: number
  createNewCount: number
  linkExistingCount: number
  selectedWithoutLicense: number  // Sprint 4f Bug 38
  licenseCounts: Record<string, number>
  licenseDeficits: Record<string, LicenseDeficit>
  usersByDomain: Record<string, number>
  estimatedTotalBytes: number
  estimatedDurationMinutes: number
}

export interface LicenseDeficit {
  skuPartNumber: string
  required: number
  available: number
  missing: number
}

export interface DestinationLicense {
  skuId: string
  skuPartNumber: string
  friendlyName: string | null
  totalUnits: number
  consumedUnits: number
  availableUnits: number
}

export interface DestinationUserSearchResult {
  id: string
  userPrincipalName: string
  displayName: string | null
  mail: string | null
  jobTitle: string | null
  isAlreadyLinked: boolean
}

export interface Wave {
  id: string
  order: number
  name: string
  description: string | null
  status: string
  parallelism: number
  estimatedDurationMinutes: number | null
  startedAt: string | null
  completedAt: string | null
  itemsTotal: number
  itemsPending: number
  itemsRunning: number
  itemsCompleted: number
  itemsFailed: number
  itemsSkipped: number
  totalBytes: number
}

export interface WavePreview {
  order: number
  name: string
  userCount: number
  bytes: number
  estimatedMinutes: number
  suggestedParallelism: number
}

export interface WavesPreviewResponse {
  waves: WavePreview[]
  totalUsers: number
  totalBytes: number
  estimatedTotalMinutes: number
}

export const tenantDomains = {
  summary: (tenantId: string, refresh = false) =>
    api.get<TenantDiscoverySummary>(`/api/tenants/${tenantId}/summary?refresh=${refresh}`),
}

export const plans = {
  ensure: (projectId: string) => api.post<Plan>(`/api/projects/${projectId}/plan`),
  get: (projectId: string) => api.get<Plan>(`/api/projects/${projectId}/plan`),
  updateDefaults: (projectId: string, data: any) =>
    api.patch<Plan>(`/api/projects/${projectId}/plan`, data),
  items: (projectId: string, params?: { domain?: string; selectedOnly?: boolean; skip?: number; take?: number }) => {
    const q = new URLSearchParams()
    if (params?.domain) q.set('domain', params.domain)
    if (params?.selectedOnly) q.set('selectedOnly', 'true')
    if (params?.skip !== undefined) q.set('skip', String(params.skip))
    if (params?.take !== undefined) q.set('take', String(params.take))
    return api.get<PlanItem[]>(`/api/projects/${projectId}/plan/items?${q.toString()}`)
  },
  patchItem: (projectId: string, itemId: string, data: any) =>
    api.patch<PlanItem>(`/api/projects/${projectId}/plan/items/${itemId}`, data),
  bulkPatch: (projectId: string, data: any) =>
    api.post<{ updated: number }>(`/api/projects/${projectId}/plan/items/bulk`, data),
  searchDestinationUsers: (projectId: string, query: string, limit = 10) =>
    api.get<DestinationUserSearchResult[]>(
      `/api/projects/${projectId}/plan/destination-users/search?q=${encodeURIComponent(query)}&limit=${limit}`),
  destinationLicenses: (projectId: string) =>
    api.get<DestinationLicense[]>(`/api/projects/${projectId}/plan/destination-licenses`),
  refreshDestinationLicenses: (projectId: string) =>
    api.post<DestinationLicense[]>(`/api/projects/${projectId}/plan/destination-licenses/refresh`),
  assignStrategy: (projectId: string, strategyId: string) =>
    api.put<{ planId: string; strategyId: string; strategyName: string }>(
      `/api/projects/${projectId}/plan/strategy`, { strategyId }),
}

// ============================================================================
// Sprint 4f — Strategies
// ============================================================================

export interface StrategyPass {
  order: number
  parallelism: number
  delayBetweenSavesMs: number
  maxRetriesPerMessage: number
  initialBackoffSeconds: number
}

export interface StrategyTemplate {
  id: string
  name: string
  description: string | null
  isFrozen: boolean
  maxSafetyAttempts: number
  safetyParallelism: number
  passes: StrategyPass[]
  totalMaxPasses: number
}

export const strategies = {
  listTemplates: () => api.get<StrategyTemplate[]>('/api/strategies/templates'),
  get: (id: string) => api.get<StrategyTemplate>(`/api/strategies/${id}`),
  createCustom: (data: {
    name: string
    description?: string
    basedOnTemplateId?: string
    maxSafetyAttempts?: number
    safetyParallelism?: number
    passes: Omit<StrategyPass, 'order'>[]
  }) => api.post<{ id: string }>('/api/strategies/custom', data),
}

// ============================================================================
// Sprint 4f — Progress & Circuit Breaker
// ============================================================================

export interface MailboxProgress {
  waveItemId: string
  sourceUpn: string
  destinationUpn: string | null
  status: number
  startedAt: string | null
  completedAt: string | null
  transferredItems: number
  transferredBytes: number
  estimatedItems: number
  estimatedBytes: number
  percentItems: number
  percentBytes: number
  transientFailures: number
  attentionFailures: number
  givenUpFailures: number   // quarentena/desistência: faltante que não entrou no destino (espelho não-fiel)
  tasks: Array<{
    taskType: number
    status: number
    phase: number | null
    transferredItems: number | null
    transferredBytes: number | null
    estimatedItems: number | null
    estimatedBytes: number | null
    errorMessage: string | null
    updatedAt: string | null
  }>
}

export interface MigrationProgressResponse {
  projectId: string
  items: MailboxProgress[]
  aggregate: {
    totalTransferredItems: number
    totalEstimatedItems: number
    totalTransferredBytes: number
    totalEstimatedBytes: number
    overallPercent: number
    elapsedSeconds: number
    bytesPerSec: number
    bytesPerHour: number
  }
}

export interface CircuitState {
  projectId: string
  state: 'Closed' | 'Open' | 'HalfOpen'
  openedAt: string | null
  openReason: string | null
  nextProbeAt: string | null
}

export const migrationReport = {
  progress: (projectId: string) =>
    api.get<MigrationProgressResponse>(`/api/projects/${projectId}/migration/progress`),
  circuitState: (projectId: string) =>
    api.get<CircuitState>(`/api/projects/${projectId}/migration/circuit-state`),
  forceCloseCircuit: (projectId: string, reason?: string) =>
    api.post<{ state: string; message: string }>(
      `/api/projects/${projectId}/migration/circuit-state/force-close`,
      { reason }),
  setProductionSide: (projectId: string, side: 'Origem' | 'Destino') =>
    api.put<{ productionSide: string }>(
      `/api/projects/${projectId}/migration/production-side`,
      { side }),
  oneDriveFailures: (projectId: string) =>
    api.get<OneDriveFailuresReport>(`/api/projects/${projectId}/migration/report/onedrive-failures`),
}

export interface OneDriveFailureFile {
  source_upn: string
  path: string
  name: string
  extension: string | null
  size_bytes: number
  http_status: number
  reason: string
  permanent: boolean
}
export interface OneDriveFailuresReport {
  project_id: string
  total: number
  permanent: number
  transient_: number
  by_account: { source_upn: string; total: number; permanent: number; transient_: number }[]
  by_extension: { extension: string; total: number; permanent: number }[]
  files: OneDriveFailureFile[]
}

export const waves = {
  list: (projectId: string) => api.get<Wave[]>(`/api/projects/${projectId}/waves`),
  preview: (projectId: string) =>
    api.get<WavesPreviewResponse>(`/api/projects/${projectId}/waves/preview`),
  approve: (projectId: string, data: any) =>
    api.post<Wave[]>(`/api/projects/${projectId}/waves/approve`, data),
  // Sprint 9: move um usuário (wave_item) pra outra onda (existente ou nova)
  moveItem: (projectId: string, waveItemId: string, data: { targetWaveId?: string; createNewWave?: boolean; newWaveName?: string }) =>
    api.put<Wave[]>(`/api/projects/${projectId}/waves/items/${waveItemId}/move`, data),
}

// ============================================================================
// Sprint 4 — Execution
// ============================================================================

export interface MigrationTaskDto {
  id: string
  taskType: string
  status: string
  startedAt?: string | null
  completedAt?: string | null
  totalItems?: number | null
  completedItems?: number | null
  failedItems?: number | null
  bytesTransferred?: number | null
  errorMessage?: string | null
}

export interface WaveItemDetail {
  id: string
  waveId: string
  planItemId: string
  sourceUpn: string
  destinationUpn?: string | null
  destinationUserObjectId?: string | null
  status: string
  startedAt?: string | null
  completedAt?: string | null
  errorSummary?: string | null
  retryCount: number
  reconciliationStatus?: string | null
  licenseAssigned: boolean
  provisionedAt?: string | null
  provisioningError?: string | null
  // Sinal único de progresso (verdade derivada no servidor — a UI só exibe).
  activePhase?: string | null
  liveCopiedItems?: number
  lastCopyAt?: string | null
  secondsSinceLastCopy?: number | null
  progressSummary?: string
  tasks: MigrationTaskDto[]
}

export interface WaveSummary {
  id: string
  order: number
  name: string
  description?: string | null
  status: string
  parallelism: number
  startedAt?: string | null
  completedAt?: string | null
  totalItems: number
  completedItems: number
  failedItems: number
  partialItems: number
  runningItems: number
  pendingItems: number
}

export interface ExecutionStatus {
  projectId: string
  planId: string
  planStatus: string
  isRunning: boolean
  isPaused: boolean
  executionStartedAt?: string | null
  executionCompletedAt?: string | null
  pausedAt?: string | null
  totalItems: number
  completedItems: number
  failedItems: number
  partialItems: number
  runningItems: number
  pendingItems: number
  skippedItems: number
  provisionedUsers: number
  failedProvisionings: number
  totalUsersToProvision: number
  currentWaveId?: string | null
  currentWaveName?: string | null
  currentWaveOrder?: number | null
  ewsLoad: EwsLoad
  idle: IdleMeter
  waves: WaveSummary[]
}

export interface IdleMeter {
  active: boolean
  seconds?: number | null
  context: string
  severity: 'ok' | 'warn' | 'alarm'
}

export interface EwsLoad {
  concurrentAccounts: number
  concurrentMessages: number
  messageParallelism: number
  score: number
  level: 'light' | 'moderate' | 'heavy'
  backoffSeconds: number
  throttleEvents: number
  windowSeconds: number
}

export interface ExecutionStartRequest {
  provisionalPassword: string
  forcePasswordChange: boolean
}

export const execution = {
  start: (projectId: string, data: ExecutionStartRequest) =>
    api.post<ExecutionStatus>(`/api/projects/${projectId}/execution/start`, data),
  pause: (projectId: string) =>
    api.post<ExecutionStatus>(`/api/projects/${projectId}/execution/pause`, {}),
  resume: (projectId: string) =>
    api.post<ExecutionStatus>(`/api/projects/${projectId}/execution/resume`, {}),
  cancel: (projectId: string) =>
    api.post<ExecutionStatus>(`/api/projects/${projectId}/execution/cancel`, {}),
  status: (projectId: string) =>
    api.get<ExecutionStatus>(`/api/projects/${projectId}/execution/status`),
  waveItems: (projectId: string, waveId: string) =>
    api.get<WaveItemDetail[]>(`/api/projects/${projectId}/execution/waves/${waveId}/items`),
  waveItem: (projectId: string, waveItemId: string) =>
    api.get<WaveItemDetail>(`/api/projects/${projectId}/execution/items/${waveItemId}`),
  setWaveParallelism: (projectId: string, waveId: string, parallelism: number) =>
    api.put<ExecutionStatus>(`/api/projects/${projectId}/execution/waves/${waveId}/parallelism`, { parallelism }),
  setMessageParallelism: (projectId: string, parallelism: number) =>
    api.put<ExecutionStatus>(`/api/projects/${projectId}/execution/message-parallelism`, { parallelism }),
}
